import { mdiClose, mdiMagnify, mdiTrayArrowUp } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import { ReconsiderationDocument } from '../../../services/worker-configuration/automation/worker-automation.types';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { ConfigurationFieldLabel } from './configuration-action-summary';
import * as S from './configuration-rule.styles';

interface IConfigurationReconsiderationDocumentsProps {
    disabled?: boolean;
    documents: ReconsiderationDocument[];
    ruleId: string;
    store: WorkerStore;
}

const DOCUMENT_UPLOAD_ACCEPT =
    'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*';

export const ConfigurationReconsiderationDocuments: React.FC<IConfigurationReconsiderationDocumentsProps> = observer(
    ({ disabled, documents, ruleId, store }) => {
        const pickerKey = `${ruleId}|documents`;
        const isOpen = store.openPickerKey === pickerKey;
        const existingIds = new Set(documents.map(doc => doc.documentId));
        const uploadInputRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            const handle = window.setTimeout(() => {
                void store.searchPickerDocuments(store.pickerQuery);
            }, 250);

            return () => window.clearTimeout(handle);
        }, [isOpen, store, store.pickerQuery]);

        const options = useMemo(() => {
            if (!isOpen) {
                return [];
            }

            return store.pickerDocumentOptions.filter(doc => !existingIds.has(doc.documentId));
        }, [existingIds, isOpen, store.pickerDocumentOptions]);

        const handlePick = (document: ReconsiderationDocument): void => {
            store.addReconsiderationDocument(ruleId, document);
        };

        const handleUploadClick = (): void => {
            if (disabled || store.documentUploading) {
                return;
            }

            uploadInputRef.current?.click();
        };

        const handleUploadChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            event.target.value = '';

            if (!file || disabled || store.documentUploading) {
                return;
            }

            void store.uploadReconsiderationDocumentFile(ruleId, file);
        };

        return (
            <div>
                <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.documents} optional>
                    Additional documentation
                </ConfigurationFieldLabel>
                <S.DocumentChipRow>
                    {documents.map((document, index) => (
                        <S.DocumentChip key={document.documentId}>
                            {document.name}
                            <S.ChipRemove
                                type="button"
                                disabled={disabled}
                                aria-label={`Remove ${document.name}`}
                                onClick={() => store.removeReconsiderationDocument(ruleId, index)}>
                                <Icon path={mdiClose} size={0.55} aria-hidden={true} />
                            </S.ChipRemove>
                        </S.DocumentChip>
                    ))}

                    <S.DocumentSelectorRow>
                        <S.DocumentChipInputWrap>
                            <Icon path={mdiMagnify} size={0.6} color="#8e9293" aria-hidden={true} />
                            <S.ChipInput
                                placeholder="Search document library…"
                                disabled={disabled}
                                readOnly={disabled}
                                value={isOpen ? store.pickerQuery : ''}
                                onFocus={event => {
                                    if (!disabled) {
                                        store.setPickerState(pickerKey, event.target.value);
                                    }
                                }}
                                onChange={event => {
                                    if (!disabled) {
                                        store.setPickerState(pickerKey, event.target.value);
                                    }
                                }}
                                onBlur={() => {
                                    window.setTimeout(() => {
                                        if (store.openPickerKey === pickerKey) {
                                            store.setPickerState(null);
                                        }
                                    }, 170);
                                }}
                                onKeyDown={event => {
                                    if (disabled) {
                                        return;
                                    }

                                    if (event.key === 'Enter' && options[0]) {
                                        event.preventDefault();
                                        handlePick(options[0]);
                                    }
                                }}
                            />
                        </S.DocumentChipInputWrap>
                        <S.DocumentUploadInput
                            ref={uploadInputRef}
                            type="file"
                            accept={DOCUMENT_UPLOAD_ACCEPT}
                            disabled={disabled || store.documentUploading}
                            onChange={handleUploadChange}
                        />
                        <S.DocumentUploadButton
                            type="button"
                            disabled={disabled || store.documentUploading}
                            onClick={handleUploadClick}>
                            <Icon path={mdiTrayArrowUp} size={0.65} aria-hidden={true} />
                            {store.documentUploading ? 'Uploading…' : 'Upload'}
                        </S.DocumentUploadButton>
                    </S.DocumentSelectorRow>
                </S.DocumentChipRow>

                {isOpen && !disabled && (
                    <S.DocumentPickerPanel>
                        <S.PickerDropdown style={{ position: 'static', marginTop: 0 }}>
                            {store.pickerSearchLoading ? (
                                <S.PickerOption type="button" disabled>
                                    <S.PickerCode>Searching documents…</S.PickerCode>
                                </S.PickerOption>
                            ) : options.length > 0 ? (
                                options.map(option => (
                                    <S.PickerOption
                                        key={option.documentId}
                                        type="button"
                                        onMouseDown={event => {
                                            event.preventDefault();
                                            handlePick(option);
                                        }}>
                                        <S.PickerCode>{option.name}</S.PickerCode>
                                        <S.PickerDesc>{option.documentId}</S.PickerDesc>
                                    </S.PickerOption>
                                ))
                            ) : store.pickerQuery.trim() ? (
                                <S.PickerOption type="button" disabled>
                                    <S.PickerCode>No documents found</S.PickerCode>
                                </S.PickerOption>
                            ) : null}
                        </S.PickerDropdown>
                    </S.DocumentPickerPanel>
                )}
            </div>
        );
    },
);
